/**
 * A plate in 3D: the controls (plate page → Animation tab).
 *
 *   ┌ 3D plate [on] ─────────────────────────────────────────────┐
 *   │ Ready moves: Studio swing · Flip in · Drop in · Spin …      │
 *   │ Describe the animation (words + a reference clip)  [Make]  │
 *   │ ▸ Resting angle  ▸ Fly in  ▸ Fly out  ▸ Slab & camera       │
 *   │ ▸ Behind it (studio, picture, colour…)  ▸ Words in 3D       │
 *
 * Every change shows in the live preview at once (no trip to the server,
 * except a studio picture). The settings are the server's PlateStage3D fields.
 */

import React, { useState, useEffect, useRef } from 'react';
import { FiBox, FiLoader, FiUpload, FiTrash2, FiZap, FiFilm, FiChevronRight, FiRotateCcw, FiCheck } from 'react-icons/fi';
import { fullStoryVideoService as api } from '../../services/apiService';
import { useT, useLanguageStore } from '../../utils/i18n';
import { useAuthStore } from '../../utils/store';
import { showError } from '../FullStory/shared';

// The server's defaults (PlateStage3D.DEFAULTS).
export const STAGE3D_DEFAULTS = {
  on: false, rx: 4, ry: -10, rz: -2, x: 0, y: 0, scale: 0.82,
  inRx: 14, inRy: -62, inRz: -14, inX: 0.55, inY: -0.05, inZ: 0.35, inSeconds: 1.1, ease: 'smooth', out: 'reverse', outSeconds: 0.8,
  thickness: 60, edge: 'auto', fov: 38, blur: 60, drift: 40,
  backdrop: 'studio', image: null, tone: 'auto', color: '#101418', rails: true, railsY: 0.78, reflect: 35, shadow: 50,
  textLayer: true, textIn: 'fly', textRx: 0, textRy: -6, textRz: -3, textLift: 30,
  textInRx: 20, textInRy: -50, textInRz: -10, textInX: 0.25, textInY: 0, textInZ: 0.3, textDelay: 0.05, textSeconds: 0.6, textEase: 'snap',
};

// Ready-made moves: a starting point, every value changeable after.
const PRESETS = {
  studioSwing: { rx: 4, ry: -12, rz: -2, x: -0.01, y: 0, scale: 0.8, inRx: 14, inRy: -62, inRz: -14, inX: 0.55, inY: -0.05, inZ: 0.35, inSeconds: 1.1, ease: 'smooth', out: 'reverse', blur: 60, drift: 40, backdrop: 'studio', rails: true, textIn: 'fly', textRy: -6, textRz: -3, textInRy: -50, textInRx: 20, textInZ: 0.3 },
  flipIn: { rx: 0, ry: -6, rz: 0, x: 0, y: 0, scale: 0.84, inRx: 0, inRy: -180, inRz: 0, inX: 0, inY: 0, inZ: -0.4, inSeconds: 1.2, ease: 'overshoot', out: 'mirror', blur: 50, drift: 25 },
  dropIn: { rx: 6, ry: -4, rz: 0, x: 0, y: 0, scale: 0.84, inRx: -80, inRy: 0, inRz: 6, inX: 0, inY: -1.1, inZ: 0, inSeconds: 0.9, ease: 'overshoot', out: 'reverse', blur: 55, drift: 20 },
  spinIn: { rx: 0, ry: -8, rz: -2, x: 0, y: 0, scale: 0.84, inRx: 10, inRy: -40, inRz: -170, inX: 0, inY: 0, inZ: -1.5, inSeconds: 1.3, ease: 'smooth', out: 'reverse', blur: 70, drift: 20 },
  pushDeep: { rx: 3, ry: -8, rz: -1, x: 0, y: 0, scale: 0.84, inRx: 0, inRy: 0, inRz: 0, inX: 0, inY: 0, inZ: -2.6, inSeconds: 1.0, ease: 'snap', out: 'fade', blur: 40, drift: 30 },
  slideAngle: { rx: 2, ry: -18, rz: 0, x: -0.04, y: 0, scale: 0.8, inRx: 0, inRy: -25, inRz: 0, inX: -1.4, inY: 0, inZ: 0, inSeconds: 0.9, ease: 'snap', out: 'mirror', blur: 70, drift: 30 },
  holdAngle: { rx: 5, ry: -14, rz: -3, x: 0, y: 0, scale: 0.82, inRx: 0, inRy: 0, inRz: 0, inX: 0, inY: 0, inZ: 0, inSeconds: 0.6, ease: 'smooth', out: 'fade', blur: 0, drift: 50 },
};
const EASES = ['smooth', 'snap', 'overshoot', 'linear'];
const OUTS = ['reverse', 'mirror', 'fade', 'none'];
const BACKDROPS = ['studio', 'image', 'blur', 'colour', 'none'];

/** A number to slide or type: [min, max, step]. */
const Slider = ({ label, value, min, max, step = 1, unit = '', onChange, hint }) => {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const commit = (v) => { const n = Number(v); if (v !== '' && Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n))); };
  return (
    <label className="flex items-center gap-2 text-xs" title={hint || label}>
      <span className="w-24 flex-shrink-0 text-gray-600 truncate">{label}</span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} aria-label={label} className="flex-1 min-w-0 accent-primary-600" />
      <input
        type="number" min={min} max={max} step={step} value={text} aria-label={label}
        onChange={(e) => { setText(e.target.value); commit(e.target.value); }}
        style={{ width: '4.2rem', paddingTop: '0.15rem', paddingBottom: '0.15rem', paddingLeft: '0.35rem', paddingRight: '0.2rem' }}
        className="input text-[11px]"
      />
      <span className="w-5 text-[10px] text-gray-400">{unit}</span>
    </label>
  );
};

/** A row of choices; the chosen one filled. */
const Chips = ({ values, value, onChange, label }) => (
  <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
    {values.map(([v, text]) => (
      <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${value === v ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-400'}`}>{text}</button>
    ))}
  </div>
);

/** A colour: automatic, or any colour. */
const ColourPick = ({ label, value, onChange, allowAuto = true }) => {
  const t = useT();
  const auto = value === 'auto';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 flex-shrink-0 text-gray-600">{label}</span>
      {allowAuto && (
        <button type="button" onClick={() => onChange(auto ? '#B3121B' : 'auto')} className={`px-2 py-0.5 rounded-full border text-[11px] ${auto ? 'bg-gray-800 border-gray-800 text-white' : 'border-gray-200'}`}>{t('plateTemplates.s3.auto')}</button>
      )}
      {!auto && <input type="color" value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} aria-label={label} className="w-9 h-6 rounded border border-gray-300 cursor-pointer" />}
      {!auto && <span className="text-[10px] text-gray-500 font-mono">{value}</span>}
    </div>
  );
};

/** A folding group of settings. */
const Group = ({ title, open: startOpen = false, children }) => {
  const [open, setOpen] = useState(startOpen);
  return (
    <div className="rounded-lg border border-gray-200">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-800">
        <FiChevronRight size={13} className={`transition-transform ${open ? 'rotate-90' : ''}`} /> {title}
      </button>
      {open && <div className="px-2.5 pb-2.5 space-y-1.5">{children}</div>}
    </div>
  );
};

/** Studios to stand the plate in: pick one, upload one, or have one made from a description. */
const StudioPicker = ({ value, onPick }) => {
  const t = useT();
  const { user } = useAuthStore();
  const [studios, setStudios] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState('');
  const [startedAt, setStartedAt] = useState(0);
  const [, tick] = useState(0);
  const file = useRef(null);
  const load = () => api.plateStudios().then(setStudios).catch((e) => { setStudios([]); showError(e); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (busy !== 'ai') return undefined;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  const upload = async (f) => {
    if (!f) return;
    setBusy('upload');
    try { const s = await api.uploadPlateStudio(f, f.name.replace(/\.[^.]+$/, '')); await load(); onPick(s.id); } catch (e) { showError(e); } finally { setBusy(''); if (file.current) file.current.value = ''; }
  };
  const make = async () => {
    setBusy('ai'); setStartedAt(Date.now());
    try { const s = await api.createPlateStudio(prompt); await load(); onPick(s.id); setPrompt(''); } catch (e) { showError(e); } finally { setBusy(''); }
  };
  const remove = async (s) => {
    if (!window.confirm(t('plateTemplates.s3.studioRemoveConfirm', { name: s.name }))) return;
    try { await api.removePlateStudio(s.id); if (value === s.id) onPick(null); load(); } catch (e) { showError(e); }
  };
  const mine = (s) => s.createdBy === String(user?._id || user?.id) || ['admin', 'superadmin', 'manager'].includes(user?.role);
  return (
    <div className="space-y-2">
      {studios === null ? <p className="text-[11px] text-gray-500 inline-flex items-center gap-1"><FiLoader size={11} className="animate-spin" /> {t('plateTemplates.live.loading')}</p> : (
        <div className="grid grid-cols-3 gap-1.5">
          {studios.map((s) => (
            <div key={s.id} className="relative group">
              <button type="button" onClick={() => onPick(s.id)} title={s.name} className={`block w-full aspect-video rounded-md overflow-hidden border-2 ${value === s.id ? 'border-primary-500' : 'border-transparent hover:border-gray-300'}`}>
                <img src={api.plateStudioUrl(s.id)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
              </button>
              {mine(s) && <button type="button" onClick={() => remove(s)} title={t('plateTemplates.remove')} aria-label={t('plateTemplates.remove')} className="absolute top-0.5 right-0.5 hidden group-hover:flex w-5 h-5 rounded bg-black/60 text-white items-center justify-center"><FiTrash2 size={11} /></button>}
            </div>
          ))}
          {!studios.length && <p className="col-span-3 text-[11px] text-gray-500">{t('plateTemplates.s3.studiosNone')}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <button type="button" onClick={() => file.current?.click()} disabled={Boolean(busy)} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1">{busy === 'upload' ? <FiLoader size={12} className="animate-spin" /> : <FiUpload size={12} />} {t('plateTemplates.s3.studioUpload')}</button>
      </div>
      <div className="flex items-center gap-1.5">
        <input value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} placeholder={t('plateTemplates.s3.studioPrompt')} className="input text-xs flex-1 min-w-0" style={{ paddingTop: '0.3rem', paddingBottom: '0.3rem' }} />
        <button type="button" onClick={make} disabled={Boolean(busy)} className="btn btn-primary btn-sm text-[11px] flex items-center gap-1 whitespace-nowrap">
          {busy === 'ai' ? <><FiLoader size={12} className="animate-spin" /> {Math.round((Date.now() - startedAt) / 1000)} s</> : <><FiZap size={12} /> {t('plateTemplates.s3.studioMake')}</>}
        </button>
      </div>
      <p className="text-[10px] text-gray-500">{t('plateTemplates.s3.studioHint')}</p>
    </div>
  );
};

/** "Describe the animation": words and/or a reference clip (its seconds) or picture → settings. */
const Describe = ({ plateId, value, onApply }) => {
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const [prompt, setPrompt] = useState('');
  const [file, setFile] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState(0);
  const [, tick] = useState(0);
  const [result, setResult] = useState(null);
  const input = useRef(null);
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  const video = file && /^video\//.test(file.type);
  const go = async () => {
    setBusy(true); setStartedAt(Date.now()); setResult(null);
    try {
      const made = await api.suggestPlateStage3d(plateId, { prompt, from: video ? from : '', to: video ? to : '', current: value, file });
      setResult(made);
      onApply(made.settings);
    } catch (e) { showError(e); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-2.5 space-y-2">
      <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5"><FiZap size={13} className="text-primary-600" /> {t('plateTemplates.s3.describe')}</p>
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} maxLength={1500} placeholder={t('plateTemplates.s3.describePlaceholder')} className="input text-xs resize-y" />
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <input ref={input} type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <button type="button" onClick={() => input.current?.click()} className="btn btn-outline btn-sm text-[11px] flex items-center gap-1"><FiFilm size={12} /> {file ? t('plateTemplates.s3.referenceChange') : t('plateTemplates.s3.reference')}</button>
        {file && <span className="truncate max-w-[10rem] text-gray-600" title={file.name}>{file.name}</span>}
        {file && <button type="button" onClick={() => { setFile(null); if (input.current) input.current.value = ''; }} className="text-gray-400 hover:text-red-600" aria-label={t('plateTemplates.remove')}><FiTrash2 size={12} /></button>}
      </div>
      {video && (
        <div className="flex items-center gap-1.5 text-[11px] text-gray-600">
          {t('plateTemplates.s3.from')}
          <input type="number" min={0} step={0.1} value={from} onChange={(e) => setFrom(e.target.value)} placeholder="8" className="input text-[11px]" style={{ width: '4rem', paddingTop: '0.15rem', paddingBottom: '0.15rem' }} />
          {t('plateTemplates.s3.to')}
          <input type="number" min={0} step={0.1} value={to} onChange={(e) => setTo(e.target.value)} placeholder="11" className="input text-[11px]" style={{ width: '4rem', paddingTop: '0.15rem', paddingBottom: '0.15rem' }} />
          {t('plateTemplates.s3.seconds')}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button type="button" onClick={go} disabled={busy || (!prompt.trim() && !file)} className="btn btn-primary btn-sm text-xs flex items-center gap-1">
          {busy ? <><FiLoader size={12} className="animate-spin" /> {Math.round((Date.now() - startedAt) / 1000)} s</> : <><FiZap size={12} /> {t('plateTemplates.s3.describeGo')}</>}
        </button>
        <span className="text-[10px] text-gray-500">{t('plateTemplates.s3.describeHint')}</span>
      </div>
      {result && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-gray-700 flex items-start gap-1"><FiCheck size={12} className="text-green-600 mt-0.5 flex-shrink-0" /> {lang === 'te' ? result.summary_te : result.summary}</p>
          {result.sheet && <img src={result.sheet} alt={t('plateTemplates.s3.sheet')} className="w-full rounded border border-gray-200" />}
        </div>
      )}
    </div>
  );
};

/**
 * The 3D controls. value: the plate's stage3d (any of its fields; the rest
 * are the defaults); onChange(next) — the whole settings; onTry: play it.
 */
export const Stage3DControls = ({ plateId, value, onChange, onTry }) => {
  const t = useT();
  const s = { ...STAGE3D_DEFAULTS, ...(value || {}) };
  const set = (change) => onChange({ ...s, ...change });
  const deg = '°';
  const s3 = (k) => t(`plateTemplates.s3.${k}`);
  // Tilt (X), turn (Y), roll (Z): rx… for the resting pose, inRx… for where the fly-in starts.
  const key = (prefix, a) => (prefix ? `${prefix}R${a}` : `r${a}`);
  const axes = (prefix, range, label) => ['x', 'y', 'z'].map((a) => {
    const lim = a === 'z' && range === 60 ? 30 : range;
    return (
      <Slider key={a} label={t(`plateTemplates.s3.axis${a.toUpperCase()}`, { what: label })} value={s[key(prefix, a)]} min={-lim} max={lim} unit={deg}
        onChange={(v) => set({ [key(prefix, a)]: v })} hint={s3(`axisHint${a.toUpperCase()}`)} />
    );
  });
  return (
    <section className="space-y-2.5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><FiBox size={14} /> {s3('title')}</h3>
          <p className="text-[11px] text-gray-500 mt-0.5">{s3('hint')}</p>
        </div>
        <button type="button" role="switch" aria-checked={s.on} onClick={() => { set({ on: !s.on }); onTry?.(); }} className={`relative w-10 h-5 rounded-full flex-shrink-0 transition-colors ${s.on ? 'bg-primary-600' : 'bg-gray-300'}`} aria-label={s3('title')}>
          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${s.on ? 'left-5' : 'left-0.5'}`} />
        </button>
      </div>
      {s.on && (
        <>
          <div className="space-y-1">
            <p className="text-[11px] text-gray-600">{s3('presets')}</p>
            <div className="flex flex-wrap gap-1">
              {Object.keys(PRESETS).map((id) => (
                <button key={id} type="button" onClick={() => { set(PRESETS[id]); onTry?.(); }} className="px-2 py-0.5 rounded-full text-[11px] border border-gray-200 bg-white hover:border-primary-400 hover:text-primary-700">{t(`plateTemplates.s3.preset.${id}`)}</button>
              ))}
              <button type="button" onClick={() => { onChange({ ...STAGE3D_DEFAULTS, on: true, image: s.image }); onTry?.(); }} className="px-2 py-0.5 rounded-full text-[11px] border border-dashed border-gray-300 text-gray-600 inline-flex items-center gap-1"><FiRotateCcw size={10} /> {s3('reset')}</button>
            </div>
          </div>

          <Describe plateId={plateId} value={s} onApply={(next) => { onChange({ ...next, on: true, image: s.image ?? next.image }); onTry?.(); }} />

          <Group title={s3('rest')} open>
            {axes('', 60, s3('plate'))}
            <Slider label={s3('across')} value={s.x} min={-0.5} max={0.5} step={0.01} onChange={(v) => set({ x: v })} />
            <Slider label={s3('upDown')} value={s.y} min={-0.5} max={0.5} step={0.01} onChange={(v) => set({ y: v })} />
            <Slider label={s3('size')} value={s.scale} min={0.3} max={1.3} step={0.01} onChange={(v) => set({ scale: v })} />
          </Group>

          <Group title={s3('flyIn')}>
            {axes('in', 180, s3('start'))}
            <Slider label={s3('across')} value={s.inX} min={-1.5} max={1.5} step={0.01} onChange={(v) => set({ inX: v })} />
            <Slider label={s3('upDown')} value={s.inY} min={-1.5} max={1.5} step={0.01} onChange={(v) => set({ inY: v })} />
            <Slider label={s3('depth')} value={s.inZ} min={-3} max={0.6} step={0.01} onChange={(v) => set({ inZ: v })} hint={s3('depthHint')} />
            <Slider label={s3('length')} value={s.inSeconds} min={0.2} max={4} step={0.05} unit="s" onChange={(v) => set({ inSeconds: v })} />
            <Chips label={s3('ease')} values={EASES.map((v) => [v, t(`plateTemplates.s3.easeName.${v}`)])} value={s.ease} onChange={(v) => { set({ ease: v }); onTry?.(); }} />
          </Group>

          <Group title={s3('flyOut')}>
            <Chips label={s3('flyOut')} values={OUTS.map((v) => [v, t(`plateTemplates.s3.out.${v}`)])} value={s.out} onChange={(v) => set({ out: v })} />
            <Slider label={s3('length')} value={s.outSeconds} min={0.2} max={4} step={0.05} unit="s" onChange={(v) => set({ outSeconds: v })} />
          </Group>

          <Group title={s3('slab')}>
            <Slider label={s3('thickness')} value={s.thickness} min={0} max={200} unit="px" onChange={(v) => set({ thickness: v })} />
            <ColourPick label={s3('edge')} value={s.edge} onChange={(v) => set({ edge: v })} />
            <Slider label={s3('lens')} value={s.fov} min={15} max={90} unit={deg} onChange={(v) => set({ fov: v })} hint={s3('lensHint')} />
            <Slider label={s3('blur')} value={s.blur} min={0} max={100} onChange={(v) => set({ blur: v })} />
            <Slider label={s3('drift')} value={s.drift} min={0} max={100} onChange={(v) => set({ drift: v })} />
          </Group>

          <Group title={s3('behind')}>
            <Chips label={s3('behind')} values={BACKDROPS.map((v) => [v, t(`plateTemplates.s3.backdrop.${v}`)])} value={s.backdrop} onChange={(v) => set({ backdrop: v })} />
            {s.backdrop === 'studio' && <ColourPick label={s3('tone')} value={s.tone} onChange={(v) => set({ tone: v })} />}
            {s.backdrop === 'image' && <StudioPicker value={s.image} onPick={(id) => set({ image: id, backdrop: id ? 'image' : 'studio' })} />}
            {s.backdrop === 'colour' && <ColourPick label={s3('colour')} value={s.color} onChange={(v) => set({ color: v })} allowAuto={false} />}
            {s.backdrop === 'none' && <p className="text-[11px] text-gray-500">{s3('noneHint')}</p>}
            <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={s.rails} onChange={(e) => set({ rails: e.target.checked })} className="rounded border-gray-300" /> {s3('rails')}</label>
            {s.rails && <Slider label={s3('railsY')} value={s.railsY} min={0.5} max={0.95} step={0.01} onChange={(v) => set({ railsY: v })} />}
            <Slider label={s3('reflect')} value={s.reflect} min={0} max={100} onChange={(v) => set({ reflect: v })} />
            <Slider label={s3('shadow')} value={s.shadow} min={0} max={100} onChange={(v) => set({ shadow: v })} />
          </Group>

          <Group title={s3('words')}>
            <label className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={s.textLayer} onChange={(e) => { set({ textLayer: e.target.checked }); onTry?.(); }} className="rounded border-gray-300" /> {s3('textLayer')}</label>
            <p className="text-[10px] text-gray-500">{s3('textLayerHint')}</p>
            {s.textLayer && (
              <>
                {['X', 'Y', 'Z'].map((a) => (
                  <Slider key={a} label={t(`plateTemplates.s3.axis${a}`, { what: s3('wordsShort') })} value={s[`textR${a.toLowerCase()}`]} min={a === 'Z' ? -30 : -60} max={a === 'Z' ? 30 : 60} unit={deg} onChange={(v) => set({ [`textR${a.toLowerCase()}`]: v })} hint={s3(`axisHint${a}`)} />
                ))}
                <Slider label={s3('lift')} value={s.textLift} min={0} max={300} unit="px" onChange={(v) => set({ textLift: v })} hint={s3('liftHint')} />
                <Chips label={s3('textIn')} values={['fly', 'anim'].map((v) => [v, t(`plateTemplates.s3.textInName.${v}`)])} value={s.textIn} onChange={(v) => { set({ textIn: v }); onTry?.(); }} />
                {s.textIn === 'fly' && (
                  <>
                    {['X', 'Y', 'Z'].map((a) => (
                      <Slider key={a} label={t(`plateTemplates.s3.axis${a}`, { what: s3('start') })} value={s[`textInR${a.toLowerCase()}`]} min={-180} max={180} unit={deg} onChange={(v) => set({ [`textInR${a.toLowerCase()}`]: v })} />
                    ))}
                    <Slider label={s3('across')} value={s.textInX} min={-1.5} max={1.5} step={0.01} onChange={(v) => set({ textInX: v })} />
                    <Slider label={s3('upDown')} value={s.textInY} min={-1.5} max={1.5} step={0.01} onChange={(v) => set({ textInY: v })} />
                    <Slider label={s3('depth')} value={s.textInZ} min={-2} max={0.5} step={0.01} onChange={(v) => set({ textInZ: v })} hint={s3('depthHint')} />
                    <Slider label={s3('delay')} value={s.textDelay} min={0} max={3} step={0.05} unit="s" onChange={(v) => set({ textDelay: v })} hint={s3('delayHint')} />
                    <Slider label={s3('length')} value={s.textSeconds} min={0.2} max={3} step={0.05} unit="s" onChange={(v) => set({ textSeconds: v })} />
                    <Chips label={s3('ease')} values={EASES.map((v) => [v, t(`plateTemplates.s3.easeName.${v}`)])} value={s.textEase} onChange={(v) => { set({ textEase: v }); onTry?.(); }} />
                  </>
                )}
              </>
            )}
          </Group>
        </>
      )}
    </section>
  );
};

export default Stage3DControls;
